import { fmtMultiplier } from '../shared/formatters'

const markupOverCost = (price, cost) => (
  price != null && cost != null && cost !== 0
    ? (price - cost) / cost
    : null
);

const resolveCost = ({ source, cmvAgg, i, usePriceCostAsCMV }) => (
  usePriceCostAsCMV
    ? (cmvAgg?.[i]?._cmvUnitCost || source?._unitCost)
    : source?._unitCost
);

const resolveCostTotal = ({ hybridAgg, cmvAgg, i, usePriceCostAsCMV }) => {
  const total = usePriceCostAsCMV ? cmvAgg?.[i]?._cmvTotal : null;
  if (total != null) return total;

  const unitsSold = hybridAgg[i]?._unitsSold;
  const unitCost = hybridAgg[i]?._unitCost;

  return (unitsSold != null && unitCost != null) ? unitsSold * unitCost : null;
};

const buildSemesterSummary = ({ retailByMonth, costByMonth, summaryPeriods = [] }) => {
  const rawValues = summaryPeriods.map(({ monthIndexes }) => {
    let retail = 0;
    let cost = 0;
    let hasData = false;

    for (const i of monthIndexes) {
      const monthRetail = retailByMonth[i];
      const monthCost = costByMonth[i];
      if (monthRetail == null || monthCost == null || monthCost <= 0) continue;

      retail += monthRetail;
      cost += monthCost;
      hasData = true;
    }

    return hasData ? retail / cost - 1 : null;
  });

  return {
    semesterValuesSummary: rawValues.map(fmtMultiplier),
    semRawValues: rawValues,
  };
};

const buildMarkupRow = ({
  monthKeys,
  hybridAgg,
  cmvAgg,
  priceField,
  usePriceCostAsCMV = true,
}) => {
  const rawValues = monthKeys.map((_, i) => {
    const source = hybridAgg[i];
    const price = source?.[priceField];
    const cost = resolveCost({ source, cmvAgg, i, usePriceCostAsCMV });

    return markupOverCost(price, cost);
  });

  return {
    values: rawValues.map(fmtMultiplier),
    rawValues,
  };
};

const buildMarkupInicialRow = ({
  monthKeys,
  hybridAgg,
  pecasAgg,
  reaisAgg,
  cmvAgg,
  originalPriceRawValues,
  summaryPeriods,
  usePriceCostAsCMV = true,
}) => {
  const monthlyRow = buildMarkupRow({
    monthKeys, hybridAgg, cmvAgg, usePriceCostAsCMV, priceField: '_originalPrice'
  });

  const retailByMonth = monthKeys.map((_, i) => {
    const stockValue = reaisAgg?.[i]?._initialInventoryValue;
    const inboundUnits = pecasAgg?.[i]?._inboundInventoryUnits ?? 0;
    const price = originalPriceRawValues?.[i];

    if (stockValue == null || (inboundUnits > 0 && price == null)) return null;

    return stockValue + inboundUnits * (price ?? 0);
  });

  const costByMonth = monthKeys.map((_, i) => {
    const stockUnits = pecasAgg?.[i]?._initialInventoryUnits;
    const inboundUnits = pecasAgg?.[i]?._inboundInventoryUnits ?? 0;
    const unitCost = resolveCost({ source: hybridAgg[i], cmvAgg, i, usePriceCostAsCMV });

    return stockUnits != null && unitCost != null ? (stockUnits + inboundUnits) * unitCost : null;
  });

  return { ...monthlyRow, ...buildSemesterSummary({ retailByMonth, costByMonth, summaryPeriods }) };
};

const buildMarkupFinalRow = ({
  monthKeys,
  hybridAgg,
  cmvAgg,
  revenueRawValues,
  summaryPeriods,
  usePriceCostAsCMV = true,
}) => {
  const monthlyRow = buildMarkupRow({
    monthKeys, hybridAgg, cmvAgg, usePriceCostAsCMV, priceField: '_salePrice'
  });

  const retailByMonth = monthKeys.map((_, i) => revenueRawValues?.[i] ?? null);

  const costByMonth = monthKeys.map((_, i) =>
    resolveCostTotal({ hybridAgg, cmvAgg, i, usePriceCostAsCMV })
  );

  return { ...monthlyRow, ...buildSemesterSummary({ retailByMonth, costByMonth, summaryPeriods }) };
};

export const markup = {
  buildMarkupInicialRow,
  buildMarkupFinalRow
}